/* Standings are derived on the client from portfolios alone, so a live backend
   only has to publish facts (equity, fills, reasoning) and never has to agree
   with the UI about ranking, ROI or house scoring. */

import { AGENTS, RULES } from '../config/competition'
import type { HouseId, HouseStanding, LeaderRow, Portfolio } from '../core/types'

export function deriveLeaderboard(
  portfolios: Record<string, Portfolio>,
  prevRanks: Record<string, number>,
): LeaderRow[] {
  const rows = AGENTS.map((a) => portfolios[a.id])
    .filter(Boolean)
    .sort((x, y) => y.equity - x.equity)
  return rows.map((p, i) => ({
    rank: i + 1,
    prevRank: prevRanks[p.agentId] ?? i + 1,
    agentId: p.agentId,
    equity: p.equity,
    pnl: p.equity - p.startEquity,
    roiPct: p.roiPct,
    winRatePct: p.winRatePct,
    tradeCount: p.tradeCount,
    maxDrawdownPct: p.maxDrawdownPct,
    sharpe: p.sharpe,
  }))
}

export function deriveHouses(portfolios: Record<string, Portfolio>): HouseStanding[] {
  const houses: HouseStanding[] = (['pumpfun', 'fomo'] as HouseId[]).map((house) => {
    const members = AGENTS.filter((a) => a.house === house)
    const equity = members.reduce((s, a) => s + (portfolios[a.id]?.equity ?? 0), 0)
    const start = members.length * RULES.seedCapital
    return {
      house,
      equity,
      pnl: equity - start,
      roiPct: start ? (equity / start - 1) * 100 : 0,
      tradeCount: members.reduce((s, a) => s + (portfolios[a.id]?.tradeCount ?? 0), 0),
      dominance: 0,
    }
  })
  // A smooth gap in ROI: 12 points of separation reads as near-total control,
  // and a mere sign flip does not peg the bar.
  houses[0].dominance = Math.tanh((houses[0].roiPct - houses[1].roiPct) / 12)
  houses[1].dominance = -houses[0].dominance
  return houses
}
